'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import ChevronDown from '@/components/icons/chevron-down'
import ChevronRightIcon from '@/components/icons/chevron-right'
import Discord from '@/components/icons/logos/discord'
import Facebook from '@/components/icons/logos/facebook'
import FacebookGamingIcon from '@/components/icons/logos/facebook-gaming'
import Instagram from '@/components/icons/logos/instagram'
import SteamIcon from '@/components/icons/logos/steam'
import Tiktok from '@/components/icons/logos/tiktok'
import Twitch from '@/components/icons/logos/twitch'
import Twitter from '@/components/icons/logos/twitter'
import WebIcon from '@/components/icons/logos/web'
import Youtube from '@/components/icons/logos/youtube'
import XboxIcon from '@/components/icons/platforms/xbox-one'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { countryISOMapping } from '@/helpers/countries'
import getUserName from '@/helpers/get-user-name'
import { getUserProfilePictureUrl } from '@/helpers/user'
import { linkServices } from '@/resources/link-services'
import { getProfileLinkUrl } from '@/resources/profile-link-services'
import {
  getPublicConnectedAccountProfileUrl,
  getPublicConnectedAccountService,
} from '@/resources/public-connected-accounts'
import upperFirst from 'lodash/upperFirst'
import Image from 'next/image'
import Link from 'next/link'
import { useMemo, useState } from 'react'

const ICONS = {
  twitch: <Twitch className="fill-neutral-blue-300" />,
  'discord-user': <Discord className="fill-neutral-blue-300" />,
  'discord-server': <Discord className="fill-neutral-blue-300" />,
  tiktok: <Tiktok className="fill-neutral-blue-300" />,
  twitter: <Twitter className="fill-neutral-blue-300" />,
  youtube: <Youtube className="fill-neutral-blue-300" />,
  instagram: <Instagram className="fill-neutral-blue-300" />,
  facebook: <Facebook className="fill-neutral-blue-300" />,
  website: <WebIcon className="stroke-neutral-blue-300" />,
  'xbox-live': <XboxIcon className="fill-neutral-blue-300" />,
  steam: <SteamIcon className="fill-neutral-blue-300" />,
  'facebook-gaming': <FacebookGamingIcon className="fill-neutral-blue-300" />,
  iracing: (
    <Image
      alt=""
      src="/img/points-import/iracing-icon.webp"
      width={48}
      height={36}
      className="h-5 w-auto object-contain"
    />
  ),
  ubisoft: (
    <Image
      alt=""
      src="/img/points-import/ubisoft-icon.webp"
      width={41}
      height={43}
      className="h-5 w-auto object-contain"
    />
  ),
}

function SocialIcon({ socialProfile, link, icon, label }) {
  if (socialProfile) {
    const foundService = linkServices.find(
      (service) => service.id === socialProfile.service,
    )
    const profileUrl = getProfileLinkUrl(
      socialProfile.service,
      socialProfile.username,
    )

    if (foundService && profileUrl && ICONS[foundService.id]) {
      return (
        <a
          data-tip={socialProfile.username}
          title={socialProfile.username}
          aria-label={`${foundService.nicename}: ${socialProfile.username}`}
          href={profileUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="relative flex h-5 items-center justify-center rounded-sm after:pointer-events-none after:absolute after:bottom-[calc(100%+8px)] after:left-1/2 after:z-40 after:hidden after:-translate-x-1/2 after:whitespace-nowrap after:rounded after:bg-neutral-blue-900 after:px-2 after:py-1 after:text-xs after:text-shades-0 after:shadow-sm hover:after:block"
        >
          {ICONS[socialProfile.service]}
        </a>
      )
    }
  }

  if (link && icon && label) {
    return (
      <a
        data-tip={label}
        title={label}
        aria-label={label}
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="relative flex h-5 items-center justify-center rounded-sm after:pointer-events-none after:absolute after:bottom-[calc(100%+8px)] after:left-1/2 after:z-40 after:hidden after:-translate-x-1/2 after:whitespace-nowrap after:rounded after:bg-neutral-blue-900 after:px-2 after:py-1 after:text-xs after:text-shades-0 after:shadow-sm hover:after:block"
      >
        {icon}
      </a>
    )
  }
}

function ConnectedAccountIcon({ account }) {
  const service = getPublicConnectedAccountService(account.serviceId)
  const profileUrl = getPublicConnectedAccountProfileUrl(account)
  const icon = ICONS[account.serviceId]

  if (!service || !icon) return null

  const accountName =
    account.serviceAccountName ||
    account.serviceAccountId ||
    'Connected account'
  const label = `${service.nicename}: ${accountName}`
  const className =
    'relative flex h-5 items-center justify-center rounded-sm after:pointer-events-none after:absolute after:bottom-[calc(100%+8px)] after:left-1/2 after:z-40 after:hidden after:-translate-x-1/2 after:whitespace-nowrap after:rounded after:bg-neutral-blue-900 after:px-2 after:py-1 after:text-xs after:text-shades-0 after:shadow-sm hover:after:block [&>svg]:size-5'

  if (profileUrl) {
    return (
      <a
        data-tip={label}
        title={label}
        aria-label={label}
        href={profileUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        {icon}
      </a>
    )
  }

  return (
    <span
      data-tip={label}
      title={label}
      aria-label={label}
      tabIndex={0}
      className={className}
    >
      {icon}
    </span>
  )
}

export default function UserInfoCard({
  user,
  session,
  profileType = 'driver',
}) {
  const [communitiessIsOpen, setCommunitiesIsOpen] = useState(true)

  let currentProfile
  switch (profileType) {
    case 'livery':
      currentProfile = user.liveryDesignerConfig
      break
    case 'broadcaster':
      currentProfile = user.broadcasterConfig
      break
    case 'commentator':
      currentProfile = user.commentatorConfig
      break
    default:
      currentProfile = user
      break
  }

  let displayName =
    currentProfile?.displayName || getUserName(user, { profile: profileType })

  const location = [user.city, user.country].filter(Boolean).join(', ')
  const locationDetails = [location, user.timezone].filter(Boolean).join(' • ')

  const communities = useMemo(() => {
    return user.communityMemberships.map((cm) => cm.community)
  }, [user.communityMemberships])

  return (
    <div
      id="user-info-card"
      className={`user-info-card top-[340px] col-span-full flex h-min w-full shrink-0 flex-col overflow-hidden rounded text-shades-0 mobile:translate-y-[-70px] tablet:p-8 desktop-sm:sticky desktop-sm:col-span-3 desktop-sm:translate-y-[-260px] desktop-sm:border desktop-sm:border-neutral-blue-700 desktop-sm:bg-neutral-blue-900`}
    >
      <div className="flex flex-col items-center">
        {/* BIG PROFILE PIC */}
        <div className="relative mb-3 flex h-20 w-20 overflow-hidden rounded-full border-shades-0 mobile:border-2">
          <ImageWithFallback
            alt="Profile pic"
            src={getUserProfilePictureUrl(user)}
            fallbackUrl="/img/default/profile-pic.webp"
            fill
          />
        </div>

        <p className="mb-1 text-h-5 font-semibold">{displayName}</p>

        <div className="flex w-full flex-col">
          {/* LOCATION SECTION */}
          {user.country && (
            <div className="flex justify-center">
              <TooltipProvider>
                <Tooltip delayDuration={0}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      aria-label={`Location: ${locationDetails}`}
                      className="focus-visible:outline-hidden relative aspect-[1.3] h-4 overflow-hidden rounded focus-visible:ring-2 focus-visible:ring-shades-0"
                    >
                      <Image
                        alt=""
                        src={`https://flagsapi.com/${
                          countryISOMapping[user.country]
                        }/flat/64.png`}
                        fill
                        sizes="21px"
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="semibold-caps text-cap">{locationDetails}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
          )}

          {/* BUTTONS */}
          <div className="relative flex w-full flex-col gap-6 py-6 desktop-sm:flex-row">
            {user.id === session?.user?.id ? (
              <Link
                className="inline-flex flex-1 items-center justify-center gap-2 rounded-sm border border-neutral-blue-500 border-shades-0 border-transparent bg-transparent px-0 px-4 py-2 text-center text-base text-sm font-medium normal-case text-shades-0 transition-colors duration-200 hover:bg-neutral-blue-700 hover:bg-neutral-blue-700/50 disabled:cursor-not-allowed disabled:opacity-60"
                href={
                  profileType === 'driver'
                    ? `/profile-manager/driver`
                    : profileType === 'livery'
                      ? `/profile-manager/liveries`
                      : profileType === 'broadcaster'
                        ? `/profile-manager/broadcaster`
                        : profileType === 'commentator'
                          ? `/profile-manager/commentator`
                          : '/profile-manager/settings/general'
                }
              >
                {/* For driver, we always show Edit since it's default. For others, check config presence. */}
                {profileType === 'driver'
                  ? 'Edit'
                  : profileType === 'livery' && !user.liveryDesignerConfig
                    ? 'Complete'
                    : profileType === 'broadcaster' && !user.broadcasterConfig
                      ? 'Complete'
                      : profileType === 'commentator' && !user.commentatorConfig
                        ? 'Complete'
                        : 'Edit'}{' '}
                {upperFirst(profileType)} Profile
              </Link>
            ) : null}
          </div>

          {/* SOCIALS SECTION */}
          <div className="flex flex-wrap justify-center gap-6">
            {user.connectedAccounts.map((connectedAccount) => (
              <ConnectedAccountIcon
                key={connectedAccount.id}
                account={connectedAccount}
              />
            ))}
            {user.onlineProfiles.map((onlineProfile) => (
              <SocialIcon
                key={onlineProfile.id}
                socialProfile={onlineProfile}
              />
            ))}
            {user.discordUserId && (
              <SocialIcon
                icon={ICONS['discord-user']}
                link={`https://discord.com/users/${user.discordUserId}`}
                label={`Discord: ${user.discordUsername || 'Connected account'}`}
              />
            )}
          </div>
          {/* GAMERTAGS AND LEAGUES */}
          <div className="mt-5 rounded border border-neutral-blue-700 p-5 tablet:mt-0 tablet:border-0 tablet:p-0">
            <div className="divider my-4 hidden tablet:flex"></div>

            <p className="semibold-caps hidden text-cap text-neutral-blue-500 tablet:inline-block">
              Communities
            </p>
            <div
              className="flex cursor-pointer items-center justify-between tablet:hidden"
              onClick={() => setCommunitiesIsOpen(!communitiessIsOpen)}
            >
              <p className="semibold-caps text-cap text-shades-0">
                Communities
              </p>
              {communitiessIsOpen ? (
                <ChevronDown className="h-4 w-4 stroke-shades-0"></ChevronDown>
              ) : (
                <ChevronRightIcon className="h-4 w-4 stroke-shades-0"></ChevronRightIcon>
              )}
            </div>
            {communitiessIsOpen && (
              <div className="mt-6 flex flex-col items-start gap-2 tablet:mt-3">
                {!communities &&
                  [1, 2, 3].map((item) => (
                    <span
                      key={`league-loader-${item}`}
                      className="h-12 w-full animate-pulse rounded-sm bg-neutral-blue-800 text-transparent"
                    >
                      .
                    </span>
                  ))}
                {communities?.length === 0 ? (
                  <p className="text-cap text-neutral-blue-500">
                    No communities joined yet!
                  </p>
                ) : (
                  communities
                    ?.sort((a, b) => {
                      return b._count.members - a._count.members
                    })
                    .slice(0, 3)
                    .map((community) => {
                      return (
                        <Link
                          href={`https://gridfinder.com/c/${community.slug}`}
                          key={community.id}
                          className="relative -ml-3 flex w-[calc(100%+12px)] cursor-pointer items-center gap-3 rounded-sm p-3 hover:bg-neutral-blue-700"
                        >
                          <div className="relative min-h-[44px] min-w-[44px] overflow-hidden rounded-sm">
                            <ImageWithFallback
                              id={`community-logo-${community.id}`}
                              src={community.logoImgUrl}
                              fallbackUrl="/img/default/grid.png"
                              className="object-cover"
                              fill
                            />
                          </div>
                          <div className="truncate">
                            <p className="truncate text-p-2 font-semibold">
                              {community.name}
                            </p>
                            <p className="text-cap font-semibold text-neutral-blue-500">
                              {community._count.members} members
                            </p>
                          </div>
                        </Link>
                      )
                    })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
